import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Col, Container, Row } from 'reactstrap';
import type { Props } from './types';
import Pagination from '../../Pagination';
import Loader from '../../Loader';
import { compareValues } from '../../../utils/compareValues';

const SearchTable = ({
  columns,
  rows = [],
  rowKey,
  onRowClick,
  rowClickInfo,
  highlightedRow,
  paginationInfo,
  paginateLocally = true,
  searchInfo,
  onRowUpdate,
  onCellCommit,
  wrapperClassName,
  tableClassName,
  emptyMessage = 'No results found.',
  renderEmpty,
  headerActions,
  isLoading = false,
  searchData,
  onSort,
  sortState,
  saveOnChange = false,
  rowClassName,
  entryRow,
}: Props): JSX.Element => {
  const rowClickHandler = onRowClick || rowClickInfo?.callback;
  const dataSource: Array<Record<string, any>> = useMemo(
    () =>
      Array.isArray(searchData)
        ? searchData
        : Array.isArray(searchData?.results)
        ? searchData.results
        : Array.isArray(searchData?.data)
        ? searchData.data
        : rows,
    [searchData, rows]
  );

  const [editableRows, setEditableRows] = useState<Array<Record<string, any>>>([]);
  const [editRowId, setEditRowId] = useState<string | number | null>(null);
  const [editCell, setEditCell] = useState<string | number | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [internalSortState, setInternalSortState] = useState<{
    keyName: string;
    direction: 'asc' | 'desc';
  } | null>(null);

  const isMultiPage = paginationInfo ? paginationInfo.total > paginationInfo.pageSize : false;
  const useServerSort = !!onSort && isMultiPage;
  const effectiveSortState = internalSortState ?? sortState ?? null;

  const handleSortClick = (keyName: string) => {
    if (useServerSort) {
      onSort!(keyName);
      return;
    }
    setInternalSortState({
      keyName,
      direction:
        effectiveSortState?.keyName === keyName && effectiveSortState?.direction === 'asc'
          ? 'desc'
          : 'asc',
    });
  };

  useEffect(() => {
    setEditableRows(
      dataSource.map((row, index) => ({
        ...row,
        __rowKey: row?.__rowKey ?? getRowKey(row, index),
      }))
    );
  }, [dataSource]);

  const rowsSource = editableRows.length ? editableRows : dataSource;

  const sortedRows = useMemo(() => {
    if (useServerSort || !effectiveSortState) return rowsSource;
    const { keyName, direction } = effectiveSortState;
    const col = columns.find((c) => c.keyName === keyName);
    return [...rowsSource].sort((a, b) => {
      const aVal = col?.sortValue ? col.sortValue(a) : a[keyName];
      const bVal = col?.sortValue ? col.sortValue(b) : b[keyName];
      const cmp = compareValues(aVal, bVal);
      return direction === 'asc' ? cmp : -cmp;
    });
  }, [rowsSource, internalSortState, sortState, useServerSort, columns]);

  const pagedRows =
    paginationInfo && paginateLocally
      ? sortedRows.slice(
          paginationInfo.page * paginationInfo.pageSize,
          paginationInfo.page * paginationInfo.pageSize + paginationInfo.pageSize
        )
      : sortedRows;

  const mutateRow = (nextRow: Record<string, any>) => {
    setEditableRows((prev) => {
      const nextKey = String(nextRow?.id ?? nextRow?._id ?? nextRow?.__rowKey ?? '');
      const idx = prev.findIndex(
        (row) => String(row?.id ?? row?._id ?? row?.__rowKey ?? '') === nextKey && nextKey !== ''
      );
      if (idx === -1) return prev;
      const next = [...prev];
      next[idx] = { ...prev[idx], ...nextRow, __rowKey: prev[idx].__rowKey };
      return next;
    });
    if (onRowUpdate) {
      onRowUpdate(nextRow);
    }
  };

  const getRowKey = (row: Record<string, any>, index: number) => {
    if (rowKey) return rowKey(row, index);
    return row?.id ?? row?._id ?? index;
  };

  const [entryValues, setEntryValues] = useState<Record<string, any>>({});
  const entryRowRef = useRef<HTMLTableRowElement>(null);

  const clearEntry = () => {
    setEntryValues({});
    // Return focus to the first input so the user can keep entering rows.
    const firstInput = entryRowRef.current?.querySelector<HTMLElement>('input, select, textarea');
    firstInput?.focus();
  };

  // An owner that keeps the row populated through onSubmit clears it by bumping
  // resetToken once its save succeeds.
  const entryResetToken = entryRow?.resetToken;
  const lastResetToken = useRef(entryResetToken);
  useEffect(() => {
    if (entryResetToken === undefined || entryResetToken === lastResetToken.current) return;
    lastResetToken.current = entryResetToken;
    clearEntry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryResetToken]);

  const submitEntry = () => {
    if (!entryRow) return;
    const hasValue = Object.values(entryValues).some(
      (value) => value !== undefined && value !== null && String(value).trim() !== ''
    );
    if (!hasValue) return;
    // onSubmit may return false to signal failed validation, or to hold the row
    // until an async save lands and bumps resetToken; keep the row populated then.
    if (entryRow.onSubmit(entryValues) === false) return;
    clearEntry();
  };

  const handleEntryKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      submitEntry();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setEntryValues({});
    }
  };

  const renderEntryRow = () => {
    if (!entryRow) return null;
    if (entryRow.render) {
      return (
        <tr className="dt-entry-row" ref={entryRowRef}>
          {entryRow.render({
            values: entryValues,
            setValue: (keyName, value) => setEntryValues((prev) => ({ ...prev, [keyName]: value })),
            submit: submitEntry,
            handleKeyDown: handleEntryKeyDown,
          })}
        </tr>
      );
    }
    return (
      <tr className="dt-entry-row" ref={entryRowRef}>
        {columns.map((col, index) => {
          const key = col.keyName || col.title || String(index);
          const value = col.keyName ? entryValues[col.keyName] : undefined;
          if (col.renderEntryCell) {
            return (
              <td key={key}>
                {col.renderEntryCell({
                  value,
                  onChange: (nextValue) =>
                    col.keyName &&
                    setEntryValues((prev) => ({ ...prev, [col.keyName as string]: nextValue })),
                  onSubmit: submitEntry,
                })}
              </td>
            );
          }
          if (col.entryEditable && col.keyName) {
            return (
              <td key={key}>
                <input
                  type="text"
                  className="form-control"
                  style={{ height: 30, padding: '2px 6px' }}
                  value={value ?? ''}
                  placeholder={col.entryPlaceholder || col.title}
                  onChange={(event) =>
                    setEntryValues((prev) => ({
                      ...prev,
                      [col.keyName as string]: event.target.value,
                    }))
                  }
                  onKeyDown={handleEntryKeyDown}
                />
              </td>
            );
          }
          return <td key={key} />;
        })}
      </tr>
    );
  };

  const renderSearchRow = () => {
    if (!searchInfo || searchInfo.length === 0) return null;
    const cells: React.ReactNode[] = [];
    for (let i = 0; i < columns.length; i += 1) {
      const search = searchInfo.find((e) => e.index === i);
      if (search) {
        const colSpan = search.colSpan || 1;
        cells.push(
          <td className="table-search-input dt-sticky-search" colSpan={colSpan} key={i}>
            {search.element}
          </td>
        );
        i += colSpan - 1;
      } else {
        cells.push(<td key={i} className="dt-sticky-search" />);
      }
    }
    return <tr>{cells}</tr>;
  };

  const InlineEditableCell = ({
    value,
    display,
    onStartEdit,
    onCommit,
    onChangeCommit,
    onCancel,
    isEditing,
  }: {
    value: string;
    display: React.ReactNode;
    onStartEdit: () => void;
    onCommit: (nextValue: string) => void;
    onChangeCommit?: (nextValue: string) => void;
    onCancel: () => void;
    isEditing: boolean;
  }) => {
    const [draft, setDraft] = useState<string>(value);
    useEffect(() => {
      if (!isEditing) return;
      setDraft(value);
    }, [value, isEditing]);
    if (!isEditing) {
      return (
        <div
          onClick={(event) => {
            event.stopPropagation();
            onStartEdit();
          }}
          style={{ cursor: 'text' }}
        >
          {display}
        </div>
      );
    }
    return (
      <input
        type="text"
        value={draft}
        autoFocus
        onClick={(event) => event.stopPropagation()}
        onChange={(event) => {
          const nextValue = event.target.value;
          setDraft(nextValue);
          if (onChangeCommit) {
            onChangeCommit(nextValue);
          }
        }}
        onBlur={() => onCommit(draft)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') onCommit(draft);
          if (event.key === 'Escape') onCancel();
        }}
        style={{ width: '100%' }}
      />
    );
  };

  return (
    <Container fluid className="no-padding">
      <Row>
        <Col>
          {headerActions ? (
            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'flex-end' }}>
              {headerActions}
            </div>
          ) : null}
          <div className={wrapperClassName || 'table-responsive dt-table-scroll'}>
            <table className={`table${tableClassName ? ` ${tableClassName}` : ''}`}>
              <thead className="thead">
                <tr>
                  {columns.map((col) => {
                    const isSorted = effectiveSortState?.keyName === col.keyName;
                    return (
                      <th
                        key={col.keyName || col.title}
                        className={col.sortBy ? 'sortable-column' : undefined}
                        style={col.align ? { textAlign: col.align } : undefined}
                      >
                        {col.sortBy ? (
                          <button
                            type="button"
                            onClick={() => handleSortClick(col.keyName || '')}
                            style={{ textDecoration: 'none', fontWeight: 600 }}
                          >
                            {col.title}
                            {isSorted && (effectiveSortState?.direction === 'asc' ? ' ↑' : ' ↓')}
                          </button>
                        ) : (
                          col.title
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {renderSearchRow()}
                {renderEntryRow()}
                {isLoading ? null : pagedRows.length === 0 ? (
                  renderEmpty ? (
                    <tr>
                      <td colSpan={Math.max(columns.length, 1)}>{renderEmpty()}</td>
                    </tr>
                  ) : emptyMessage !== null && emptyMessage !== undefined ? (
                    <tr>
                      <td colSpan={Math.max(columns.length, 1)}>{emptyMessage}</td>
                    </tr>
                  ) : null
                ) : (
                  pagedRows.map((row: Record<string, any>, index: number) => {
                    const rowKeyValue = getRowKey(row, index);
                    const classNames = ['dr-row'];
                    if (rowClickHandler) classNames.push('clickable-datarow');
                    if (highlightedRow !== undefined && highlightedRow === rowKeyValue) {
                      classNames.push('highlighted');
                    }
                    if (rowClassName) {
                      const customClass = rowClassName(row, index);
                      if (customClass) classNames.push(customClass);
                    }
                    const isRowEditMode = isEditMode && editRowId === rowKeyValue;
                    return (
                      <tr
                        key={rowKeyValue}
                        className={classNames.join(' ')}
                        onClick={rowClickHandler ? () => rowClickHandler(row) : undefined}
                      >
                        {columns.map((col) => {
                          const cellValue = col.keyName
                            ? (row as Record<string, any>)[col.keyName]
                            : undefined;
                          if (col.editHandler) {
                            return (
                              <td
                                key={col.keyName || col.title}
                                style={col.align ? { textAlign: col.align } : undefined}
                              >
                                {col.editHandler(
                                  row,
                                  row,
                                  isRowEditMode,
                                  (next) => {
                                    setEditRowId(rowKeyValue);
                                    setIsEditMode(next);
                                    if (!next) {
                                      setEditCell(null);
                                    }
                                  },
                                  index,
                                  editCell,
                                  setEditCell,
                                  mutateRow
                                )}
                              </td>
                            );
                          }
                          if (col.cell?.component) {
                            const CellComponent = col.cell.component;
                            return (
                              <td
                                key={col.keyName || col.title}
                                style={col.align ? { textAlign: col.align } : undefined}
                              >
                                <CellComponent rowData={row} mixIns={col.cell.mixIns} />
                              </td>
                            );
                          }
                          if (col.editable && col.keyName) {
                            const isCellEditing =
                              isRowEditMode && editCell === (col.keyName || col.title);
                            const display = col.customComponent
                              ? col.customComponent(row)
                              : cellValue;
                            return (
                              <td
                                key={col.keyName || col.title}
                                style={col.align ? { textAlign: col.align } : undefined}
                              >
                                <InlineEditableCell
                                  value={String(cellValue ?? '')}
                                  display={display}
                                  isEditing={isCellEditing}
                                  onStartEdit={() => {
                                    setEditRowId(rowKeyValue);
                                    setEditCell(col.keyName || col.title);
                                    setIsEditMode(true);
                                  }}
                                  onCommit={(nextValue) => {
                                    const nextRow = { ...row, [col.keyName as string]: nextValue };
                                    mutateRow(nextRow);
                                    if (onCellCommit) {
                                      onCellCommit({
                                        row: nextRow,
                                        keyName: col.keyName as string,
                                        value: nextValue,
                                        mode: 'commit',
                                      });
                                    }
                                    setIsEditMode(false);
                                    setEditCell(null);
                                  }}
                                  onChangeCommit={
                                    saveOnChange
                                      ? (nextValue) => {
                                          const nextRow = {
                                            ...row,
                                            [col.keyName as string]: nextValue,
                                          };
                                          mutateRow(nextRow);
                                          if (onCellCommit) {
                                            onCellCommit({
                                              row: nextRow,
                                              keyName: col.keyName as string,
                                              value: nextValue,
                                              mode: 'change',
                                            });
                                          }
                                        }
                                      : undefined
                                  }
                                  onCancel={() => {
                                    setIsEditMode(false);
                                    setEditCell(null);
                                  }}
                                />
                              </td>
                            );
                          }
                          return (
                            <td
                              key={col.keyName || col.title}
                              style={col.align ? { textAlign: col.align } : undefined}
                            >
                              {col.customComponent ? col.customComponent(row) : cellValue}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {isLoading ? <Loader /> : null}
          {paginationInfo ? <Pagination info={paginationInfo} /> : null}
        </Col>
      </Row>
    </Container>
  );
};

export default SearchTable;
